/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Input, Select, Space, Card, Typography, message, Badge, Tooltip, Pagination } from 'antd';
import {
  SearchOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SafetyCertificateOutlined,
  SafetyCertificateFilled,
  ReloadOutlined,
  ShopOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { getAuthHeaders } from './jobManagementUtils';
import './JobManagement.css';

const { Text } = Typography;
const { Option } = Select;

function JobEmployerManagement(props) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const authHeaders = getAuthHeaders(props.permitdata?.token);

  const fetchEmployers = async (currentPage = page, currentSize = pageSize) => {
    setLoading(true);
    try {
      const res = await axios.post(
        (props.app_hostname || '') + '/api/account/management/jobs/employers',
        {
          keyword: searchText,
          status: statusFilter,
          page: currentPage,
          page_size: currentSize,
        },
        { headers: authHeaders }
      );
      if (res.data && res.data.status === 'Success' && res.data.data) {
        setData(res.data.data.items || []);
        setTotal(res.data.data.total || 0);
      }
    } catch (err) {
      message.error('ไม่สามารถดึงข้อมูลสถานประกอบการได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployers(1, pageSize);
  }, [statusFilter]);

  const handleSearch = () => {
    setPage(1);
    fetchEmployers(1, pageSize);
  };

  const handleToggleVerified = async (record) => {
    const nextVerified = !record.is_employer_verified;
    try {
      await axios.post(
        (props.app_hostname || '') + '/api/account/management/jobs/user_status',
        {
          shop_id: record.shop_id,
          user_id: record.id,
          status: record.status,
          is_verified: nextVerified,
        },
        { headers: authHeaders }
      );
      setData((prev) =>
        prev.map((item) => (item.shop_id === record.shop_id ? { ...item, is_employer_verified: nextVerified } : item))
      );
      message.success(nextVerified ? `อนุมัติตรา Verified Badge ให้ร้าน ${record.company_name} แล้ว` : 'ยกเลิกตรา Verified Badge');
    } catch (err) {
      message.error('เกิดข้อผิดพลาดในการปรับสถานะ');
    }
  };

  const handleStatusChange = async (record, newStatus) => {
    try {
      await axios.post(
        (props.app_hostname || '') + '/api/account/management/jobs/user_status',
        {
          shop_id: record.shop_id,
          user_id: record.id,
          status: newStatus,
        },
        { headers: authHeaders }
      );
      setData((prev) =>
        prev.map((item) => (item.shop_id === record.shop_id ? { ...item, status: newStatus } : item))
      );
      message.success('ปรับปรุงสถานะเรียบร้อยแล้ว');
    } catch (err) {
      message.error('เกิดข้อผิดพลาดในการปรับสถานะ');
    }
  };

  const columns = [
    {
      title: 'รหัสร้าน / สถานพยาบาล',
      dataIndex: 'shop_id',
      key: 'shop_id',
      width: 140,
      render: (shopId) => <Tag color='blue'><ShopOutlined style={{ marginRight: 4 }} />{shopId || '-'}</Tag>,
    },
    {
      title: 'ชื่อสถานประกอบการ / ร้านค้า',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 600, color: '#1890ff', fontSize: 14 }}>{text || record.username || '-'}</span>
            {record.is_employer_verified ? (
              <Tooltip title='ได้รับการยืนยันสถานพยาบาล/ร้านยา (Verified Employer)'>
                <SafetyCertificateFilled style={{ color: '#52c41a', fontSize: 16 }} />
              </Tooltip>
            ) : (
              <Tooltip title='ยังไม่ได้รับ Verified Badge'>
                <SafetyCertificateOutlined style={{ color: '#bfbfbf', fontSize: 16 }} />
              </Tooltip>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            Username: {record.username || '-'} | โทร: {record.phone || '-'}
          </div>
        </div>
      ),
    },
    {
      title: 'จังหวัด / ที่อยู่',
      dataIndex: 'province',
      key: 'province',
      width: 150,
      render: (prov, record) => (
        <div>
          <Tag color='cyan'>{prov || 'ไม่ระบุ'}</Tag>
          {record.district && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{record.district}</div>}
        </div>
      ),
    },
    {
      title: 'ตำแหน่งงานที่เปิดรับ',
      dataIndex: 'job_post_count',
      key: 'job_post_count',
      width: 140,
      align: 'center',
      render: (count) => (
        <Badge
          count={count || 0}
          showZero
          style={{ backgroundColor: (count || 0) > 0 ? '#52c41a' : '#d9d9d9' }}
        />
      ),
    },
    {
      title: 'สถานะบัญชี',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        if (status === 'approved' || status === 'active') {
          return <Tag color='success'>ใช้งานปกติ</Tag>;
        }
        if (status === 'suspended') {
          return <Tag color='error'>ระงับการใช้งาน</Tag>;
        }
        return <Tag color='warning'>รอการตรวจสอบ</Tag>;
      },
    },
    {
      title: 'จัดการตรา Verified & สถานะ',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size='small'>
          <Button
            size='small'
            type={record.is_employer_verified ? 'default' : 'primary'}
            icon={record.is_employer_verified ? <SafetyCertificateFilled /> : <SafetyCertificateOutlined />}
            onClick={() => handleToggleVerified(record)}
            style={record.is_employer_verified ? { borderColor: '#faad14', color: '#faad14' } : { background: '#52c41a', borderColor: '#52c41a' }}
          >
            {record.is_employer_verified ? 'ยกเลิก Verified' : 'อนุมัติ Verified'}
          </Button>

          {record.status === 'suspended' ? (
            <Button
              size='small'
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusChange(record, 'approved')}
              type='text'
              style={{ color: '#52c41a' }}
            >
              ปลดระงับ
            </Button>
          ) : (
            <Button
              size='small'
              danger
              icon={<StopOutlined />}
              onClick={() => handleStatusChange(record, 'suspended')}
              type='text'
            >
              ระงับ
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className='job-management-container'>
      <div className='job-management-header'>
        <div>
          <h2>🏢 จัดการสถานประกอบการ & ร้านค้า (Employer Directory)</h2>
          <Text type='secondary'>
            ดึงข้อมูลร้านค้า/สถานพยาบาลจริงจากระบบ ({total.toLocaleString()} แห่ง) สำหรับตรวจสอบเอกสารและมอบตรา Verified Badge
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => fetchEmployers(page, pageSize)}>
          รีเฟรช
        </Button>
      </div>

      <Card className='job-management-card'>
        <div className='job-filter-bar'>
          <Input
            placeholder='ค้นหาชื่อร้าน, รหัส SHP, เบอร์โทร...'
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 280 }}
            allowClear
          />
          <Button type='primary' icon={<SearchOutlined />} onClick={handleSearch}>
            ค้นหา
          </Button>

          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 150 }}>
            <Option value='all'>สถานะทั้งหมด</Option>
            <Option value='approved'>อนุมัติแล้ว (Approved)</Option>
            <Option value='pending'>รอตรวจ (Pending)</Option>
            <Option value='suspended'>ระงับ (Suspended)</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey={(r) => r.shop_id || r.id}
          loading={loading}
          pagination={false}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={['10', '20', '50', '100']}
            onChange={(p, s) => {
              setPage(p);
              setPageSize(s);
              fetchEmployers(p, s);
            }}
            showTotal={(t) => `พบสถานประกอบการทั้งหมด ${t.toLocaleString()} แห่ง`}
          />
        </div>
      </Card>
    </div>
  );
}

export default JobEmployerManagement;
